import Form, {
  ButtonItem,
  ButtonOptions,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import React, { useRef } from 'react';
import { useHistory } from 'react-router';
import useSWR from 'swr';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { IForm } from '../../types/iForm';
import { Person } from '../../types/person.types';
import { UserColor, UserColors } from '../../types/userColor.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { formTranslationByEdit } from '../../utils/formTranslationByEdit';
import { dxSelectBoxConfig } from '../tasks/TasksForm';

export interface UserColorsForm extends IForm {
  userColors?: UserColor[];
}

export const UserColorsForm = ({ onSubmit, formData, isEdit, userColors }: UserColorsForm) => {
  const { translate } = useTranslation();
  const transFormName = formTranslationByEdit('UserColor', isEdit);
  const { loading } = useLoading();
  const history = useHistory();
  const backRoute = '/user-colors';
  const formRef = useRef<Form>(null);
  const [prompt, setDirty, setClean, handleCloseButton] = useUnsavedChangesWarning(
    backRoute,
    () => {
      if (!formRef) return;
      if (formRef.current!.instance.validate().isValid) {
        setClean();
        onSubmit(null).then((result) => {
          if (result) history.push(backRoute);
          else setDirty();
        });
      }
    },
  );

  const { data: responsiblePersons } = useSWR<Person[]>(API_URL.PERSON_FROM_THIS_APP_LIST, fetcher);

  return (
    <form
      onSubmit={(e) => {
        onSubmit(e);
        if (formRef && formRef.current && !isEdit) formRef.current?.instance.resetValues();
        // @ts-ignore
        setClean();
      }}
    >
      <Form
        colCount={1}
        formData={formData}
        showValidationSummary={true}
        onFieldDataChanged={() => {
          // @ts-ignore
          setDirty();
        }}
        ref={formRef}
      >
        <GroupItem>
          <SimpleItem
            dataField="userId"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'personalNumber',
              displayExpr: 'fullName',
              items: userColors
                ? responsiblePersons?.filter(
                    (p) => userColors.filter((uc) => uc.userId === p.personalNumber).length === 0,
                  )
                : responsiblePersons,
              readOnly: isEdit,
            }}
          >
            <Label text={translate!('User', transFormName)} />
            <RequiredRule message={translate!('User is required', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem>
          <SimpleItem
            dataField="color"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'value',
              displayExpr: 'name',
              items: UserColors.map((color) => {
                return {
                  ...color,
                  name: translate!(color.name, 'UserColor.Color'),
                };
              }),
              itemTemplate: (data: any, _index: number, element: any) => {
                element.innerHTML = `<div style="color: #${data.value}">${data.name}</div>`;
              },
            }}
          >
            <Label text={translate!('Color', transFormName)} />
            <RequiredRule message={translate!('Color is required', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem colCount={14}>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              text={translate!('Close', 'Asset.Update')}
              type={'normal'}
              useSubmitBehavior={false}
              onClick={handleCloseButton}
            />
          </ButtonItem>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              disabled={loading}
              text={translate!('Save', transFormName)}
              type={'success'}
              useSubmitBehavior
            />
          </ButtonItem>
        </GroupItem>
        {prompt}
      </Form>
    </form>
  );
};
